import React, { useEffect, useMemo } from 'react';
import { useLang } from '../../util/lang';
import {
    smilDesignQuestionsConfig,
    smilDesignQuestionsConfigFirstFit,
    validationSchema,
    validationSchemaAlign,
    validationSchemaFirstFit,
} from './SmileDesignQuestions';
import MyTeethChoice from '../../components/ReactHookForm/TeethEquipmentRhf/MyTeethChoice';
import { FormProvider, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import SDTeethRhf from '../../components/ReactHookForm/TeethEquipmentRhf/SDTeethRhf';
import TextFieldRhf from '../../components/ReactHookForm/TextFieldRhf/TextFieldRhf';
import { useSelector } from 'react-redux';

const OneLineQuestionToAnswer = ({ question, answer }) => {
    if (!answer) return null;
    return (
        <div style={{ display: 'flex', marginBottom: 8 }}>
            <div style={{ paddingRight: 8 }}>{question}</div>
            <div style={{ fontWeight: 'bold' }}>{answer}</div>
        </div>
    );
};

const partialTranslation = (ans, get) => {
    // console.log('ans', ans);
    // console.log(`@PatientInfo.smileDesign.prescriptionNotes.${ans}`);
    if (ans !== undefined && ans !== null && ans !== '' && (ans.startsWith('@') || ans.startsWith('option'))) {
        return get(`@PatientInfo.smileDesign.prescriptionNotes.${ans}`);
    }
    return ans;
};

const translateQuestionAnswer = (question, ans, get) => {
    const answer = Array.isArray(ans) ? ans : [ans];
    const translatedAnswer = answer.map((value) => {
        const option = question?.option?.find((item) => item.value === value);
        return option?.label ? get(option.label) : partialTranslation(value, get);
    });

    return Array.isArray(ans) ? translatedAnswer.join(', ') : translatedAnswer[0];
};

// Remark: Remove the number, period, and space
// Remark: Delete afte change the transation text and deployed
const partialQuestionTranslation = (question, get, order = false) => {
    if (!question) return question;

    const cleanedText = question.startsWith('@') ? get(question).replace(/^\d+\.\s*/, '') : question;

    return order ? `${order}. ${cleanedText}` : cleanedText;
};

// Displays submitted Smile Design prescription notes in read-only mode.
const SmileDesignPrescriptionNotesPreviewMap = ({
    defaultValues,
    schema = undefined,
    isMarieGroup = false,
    isSEAGroup = false,
    isUpsellTab = false,
}) => {
    const get = useLang();
    const displayFirstFitFlag = useSelector((state) => state.globalReducer.displayFirstFitFlag);
    const effectiveIsSEAGroup = defaultValues?.isSEAGroup ?? isSEAGroup;

    // Resolve schema version from submitted payload first to avoid rendering
    // first-fit questions for legacy v1 records when global flag is enabled.
    const hasFirstFitAnswer =
        !!defaultValues?.maxillaryMidlinePosition ||
        !!defaultValues?.mandibularMidlineRelationship ||
        !!defaultValues?.maxillaryMidlineAngulation ||
        !!defaultValues?.cantingInSimulation ||
        !!defaultValues?.softTissueModification;
    const hasV1MidlineAnswer = !!defaultValues?.midlineCorrectionInSimulation;
    const effectiveFirstFitFlag = hasV1MidlineAnswer ? false : (hasFirstFitAnswer || displayFirstFitFlag);

    const activeSchema =
        schema || (effectiveFirstFitFlag ? validationSchemaFirstFit : validationSchema).concat(validationSchemaAlign);
    const methods = useForm({
        resolver: yupResolver(activeSchema),
        defaultValues: defaultValues,
    });

    const config = useMemo(() => {
        const questionConfig = effectiveFirstFitFlag ? smilDesignQuestionsConfigFirstFit : smilDesignQuestionsConfig;
        return questionConfig(defaultValues, isMarieGroup, isUpsellTab, false, effectiveIsSEAGroup);
    }, [defaultValues, effectiveFirstFitFlag, effectiveIsSEAGroup, isMarieGroup, isUpsellTab, schema]);
    const teethForSimulationQuestion = effectiveFirstFitFlag ? config[2] : config[2];
    const verticalDimensionQuestion = effectiveFirstFitFlag ? config[3] : config[3];
    const midlineCorrectionQuestion = effectiveFirstFitFlag ? null : config[4];
    const maxillaryMidlineQuestion = effectiveFirstFitFlag ? config[4] : null;
    const mandibularMidlineQuestion = effectiveFirstFitFlag ? config[5] : null;
    const midlineAngulationQuestion = effectiveFirstFitFlag ? config[6] : null;
    const cantingQuestion = effectiveFirstFitFlag ? config[7] : null;
    const softTissueQuestion = effectiveFirstFitFlag ? config[8] : null;
    const teethReductionQuestion = effectiveFirstFitFlag ? config[9] : config[5];
    const remarkQuestion = effectiveFirstFitFlag ? config[isUpsellTab ? 10 : 11] : config[7];
    const preRestorativeAlignerQuestion = effectiveFirstFitFlag ? config[10] : config[6];

    useEffect(() => {
        methods.reset(defaultValues);
    }, [defaultValues]);

    return (
        <FormProvider {...methods}>
            <div>
                <OneLineQuestionToAnswer
                    question={partialQuestionTranslation(config[1].question, get, 1)}
                    answer={partialTranslation(defaultValues?.plannedRestorationSolution, get)}
                />

                {/* <OneLineQuestionToAnswer
                    question={partialQuestionTranslation(config[1].children['2B'].question, get)}
                    answer={partialTranslation(defaultValues?.orthodonticTreatmentTiming, get)}
                />

                <OneLineQuestionToAnswer
                    question={partialQuestionTranslation(config[1].children['2C'].question, get)}
                    answer={defaultValues?.fitType?.map((value) => partialTranslation(value, get)).join(', ')}
                /> */}

                <SDTeethRhf
                    key={config[1].children['2A'].name}
                    name={config[1].children['2A'].name}
                    // disable={true}
                    showSingleError={false}
                    readOnly={true}
                    defaultValue={defaultValues?.teethSD}
                />

                {effectiveFirstFitFlag && (
                    <>
                        {/* Remark: if the defaultValues is string, then it is a single value */}
                        {!Array.isArray(defaultValues?.[`${config[2].name}`]) ? (
                            <div>
                                <div style={{ display: 'flex' }}>
                                    <div style={{ paddingRight: 8 }}>
                                        {partialQuestionTranslation(config[2].question, get, 2)}
                                    </div>
                                    <div style={{ fontWeight: 'bold' }}>
                                        {partialTranslation(defaultValues?.[`${config[2].name}`], get)}
                                    </div>
                                </div>
                                <div>
                                    <div style={{ marginLeft: 16 }}>Reference teeth image: </div>
                                    <img src={'/img/prescriptionNotes/teeth_number_CS6.ai.png'} width={'100%'} alt="cs6" />
                                </div>
                            </div>
                        ) : (
                            <>
                                {/* Remark: if the defaultValues is array, then teeth image */}
                                <MyTeethChoice
                                    key={config[2].name}
                                    question={partialQuestionTranslation(config[2].question, get, 2)}
                                    name={config[2].name}
                                    teethSelectionDisabled={true}
                                    checkSuccess={config[2].checkSuccess}
                                />
                            </>
                        )}
                    </>
                )}

                {!effectiveFirstFitFlag && (
                    <MyTeethChoice
                        key={teethForSimulationQuestion.name}
                        question={partialQuestionTranslation(
                            teethForSimulationQuestion.question,
                            get,
                            effectiveFirstFitFlag ? 2 : 2
                        )}
                        name={teethForSimulationQuestion.name}
                        teethSelectionDisabled={true}
                        checkSuccess={teethForSimulationQuestion.checkSuccess}
                    />
                )}

                {defaultValues?.[`${verticalDimensionQuestion.name}`] === 'option.notIncreaseVerticalDimension' && (
                    <OneLineQuestionToAnswer
                        question={partialQuestionTranslation(
                            verticalDimensionQuestion.question,
                            get,
                            effectiveFirstFitFlag ? 3 : 3
                        )}
                        answer={`${partialTranslation(defaultValues[`${verticalDimensionQuestion.name}`], get)}`}
                    />
                )}
                {defaultValues?.[`${verticalDimensionQuestion.name}`] === 'option.increaseVerticalDimension' && (
                    <OneLineQuestionToAnswer
                        question={partialQuestionTranslation(
                            verticalDimensionQuestion.question,
                            get,
                            effectiveFirstFitFlag ? 3 : 3
                        )}
                        answer={
                            <>
                                {partialTranslation(defaultValues[`verticalDimension`], get)}
                                {defaultValues['verticalDimensionUpperPosterior'] && (
                                    <>, {get(effectiveFirstFitFlag ? `@PatientInfo.smileDesign.prescriptionNotes.option.upperPosteriorFirstFit` : `@PatientInfo.smileDesign.prescriptionNotes.option.upperPosterior`)}: {defaultValues['verticalDimensionUpperPosterior']}</>
                                )}
                                {defaultValues['verticalDimensionLowerPosterior'] && (
                                    <>, {get(effectiveFirstFitFlag ? `@PatientInfo.smileDesign.prescriptionNotes.option.lowerPosteriorFirstFit` : `@PatientInfo.smileDesign.prescriptionNotes.option.lowerPosterior`)}: {defaultValues['verticalDimensionLowerPosterior']}</>
                                )}
                            </>
                        }
                    />
                )}

                {midlineCorrectionQuestion && (
                    <OneLineQuestionToAnswer
                        question={partialQuestionTranslation(midlineCorrectionQuestion.question, get, 4)}
                        answer={translateQuestionAnswer(
                            midlineCorrectionQuestion,
                            defaultValues?.[midlineCorrectionQuestion.name],
                            get
                        )}
                    />
                )}

                {effectiveFirstFitFlag &&
                    [maxillaryMidlineQuestion, mandibularMidlineQuestion, midlineAngulationQuestion].map((question, index) => (
                        <OneLineQuestionToAnswer
                            key={question.name}
                            question={partialQuestionTranslation(question.question, get, index + 4)}
                            answer={translateQuestionAnswer(question, defaultValues?.[question.name], get)}
                        />
                    ))}

                {effectiveFirstFitFlag && (
                    <>
                        <OneLineQuestionToAnswer
                            question={partialQuestionTranslation(cantingQuestion.question, get, 7)}
                            answer={translateQuestionAnswer(cantingQuestion, defaultValues?.[`${cantingQuestion.name}`], get)}
                        />
                    </>
                )}

                {effectiveFirstFitFlag && (
                    <OneLineQuestionToAnswer
                        question={partialQuestionTranslation(softTissueQuestion.question, get, 8)}
                        answer={translateQuestionAnswer(softTissueQuestion, defaultValues?.[`${softTissueQuestion.name}`], get)}
                    />
                )}

                {teethReductionQuestion?.children?.a && defaultValues?.[teethReductionQuestion.children.a.name] && (
                    <OneLineQuestionToAnswer
                        question={partialQuestionTranslation(
                            teethReductionQuestion.children.a.question,
                            get,
                            effectiveFirstFitFlag ? '9a' : '5a'
                        )}
                        answer={
                            <div>
                                {partialTranslation(defaultValues?.[`${teethReductionQuestion.children.a.name}`], get)}
                            </div>
                        }
                    />
                )}
                {teethReductionQuestion?.children?.b && defaultValues?.[teethReductionQuestion.children.b.name] && (
                    <OneLineQuestionToAnswer
                        question={partialQuestionTranslation(
                            teethReductionQuestion.children.b.question,
                            get,
                            effectiveFirstFitFlag ? '9b' : '5b'
                        )}
                        answer={
                            <div>
                                {partialTranslation(defaultValues?.[`${teethReductionQuestion.children.b.name}`], get)}
                            </div>
                        }
                    />
                )}

                {!isUpsellTab && preRestorativeAlignerQuestion && (
                    <>
                        <OneLineQuestionToAnswer
                            question={partialQuestionTranslation(
                                preRestorativeAlignerQuestion.question,
                                get,
                                effectiveFirstFitFlag ? 10 : 6
                            )}
                            answer={(() => {
                                const pre = defaultValues?.preRestorativeAlignerSimulation;
                                const upgrade = defaultValues?.upgradePreRestorativeAlignerSimulation;
                                if (pre === 'option.no')
                                    return get(
                                        '@PatientInfo.smileDesign.prescriptionNotes.option.preRestorativeAlignerNo'
                                    );
                                if (pre === 'option.yes' && upgrade === 'option.no')
                                    return get(
                                        '@PatientInfo.smileDesign.prescriptionNotes.option.preRestorativeAligner14Step'
                                    );
                                if (pre === 'option.yes' && upgrade === 'option.yes')
                                    return get(
                                        '@PatientInfo.smileDesign.prescriptionNotes.option.preRestorativeAlignerUnlimited'
                                    );
                                return partialTranslation(pre, get);
                            })()}
                        />
                        {defaultValues?.preRestorativeAlignerSimulation === 'option.yes' &&
                            preRestorativeAlignerQuestion.children?.[0] && (
                            <div style={{ marginTop: 32 }}>
                                <MyTeethChoice
                                    key={preRestorativeAlignerQuestion.children[0].name}
                                    question={partialQuestionTranslation(
                                        preRestorativeAlignerQuestion.children[0].question,
                                        get,
                                        false
                                    )}
                                    name={preRestorativeAlignerQuestion.children[0].name}
                                    teethSelectionDisabled={true}
                                    checkSuccess={false}
                                />
                            </div>
                        )}
                    </>
                )}

                <>
                    {remarkQuestion && (
                        <>
                            <div>{partialQuestionTranslation(remarkQuestion.question, get, effectiveFirstFitFlag ? (isUpsellTab ? 10 : 11) : 7)}</div>
                            <div>
                                <TextFieldRhf
                                    name={remarkQuestion.name}
                                    question={undefined}
                                    defaultValue={defaultValues?.remark || 'N/A'}
                                    readOnly={true}
                                />
                            </div>
                        </>
                    )}
                </>
            </div>
        </FormProvider>
    );
};

export default SmileDesignPrescriptionNotesPreviewMap;
